import React, { FC } from 'react'
import { ExternalLink, File, AlertCircle } from 'react-feather'
import get from 'lodash.get'
import clsx from 'clsx'

import {
  Tag,
  AssignmentGoogleDocAttachment,
  Attachments,
  FilePreviewV2,
  Tooltip
} from '../../atoms'

import getCommentsFromIds from './getCommentsFromIds'
import checkIfFieldIdHasTag from './checkIfFieldIdHasTag'
import styles from './_.module.css'

interface FieldKeys {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
  title: string
  type: string
  id?: string
  options?: { label: string; value: number | string }[] | BlockSelectOption[]
  guideline?: string
  label?: string
}

interface Props {
  fields: FieldKeys[][]
  data?: Assignment
  loomHeight?: number
  loomWidth?: number
  user?: UserRole
  showNewUI?: boolean
  isOpenFromModal?: boolean
}

// matches url with either http/https protocol - https://stackoverflow.com/a/3809435
const validUrl = new RegExp(
  /https?:\/\/(www\.)?[\w#%.:=@~-]{1,256}\.[\d()a-z]{1,6}\b([\w#%&()./:=?@~-]*)?/i
)

type HTMLRenderProps = {
  value: {
    richText: string
    attachments: Attachment[]
    loomUrls: string[]
  }
  loomWidth?: number
  loomHeight?: number
}

const HTMLRender = ({ value, loomWidth, loomHeight }: HTMLRenderProps) => {
  const renderFiles = () => {
    const attachments = value.attachments || []

    if (attachments.length === 0) return null

    return (
      <div className={styles.files}>
        {attachments.map(attachment => {
          return (
            <FilePreviewV2
              key={attachment.id}
              id={attachment.id}
              name={attachment.name}
            />
          )
        })}
      </div>
    )
  }

  const html =
    typeof value === 'string' ? value : get(value, 'richText', '') || ''

  return (
    <>
      <div dangerouslySetInnerHTML={{ __html: html }} />
      {Array.isArray(value.loomUrls) &&
        value.loomUrls?.map(url => {
          return <FilePreviewV2 key={url} url={url} />
        })}
      {renderFiles()}
    </>
  )
}

export const DisplayBrief: FC<Props> = ({
  fields = [],
  data: assignmentDetails,
  loomHeight,
  loomWidth,
  user,
  showNewUI = false,
  isOpenFromModal = false
}) => {
  const getDataOfCommentBox = (data?: Assignment, id?: string) => {
    if (data) {
      return {
        comment: getCommentsFromIds(data, id),
        files: data.reference_files.filter(file =>
          checkIfFieldIdHasTag(file.tag, id)
        )
      }
    }
    return {
      comment: '',
      files: []
    }
  }

  const renderContentByType = (detail: FieldKeys, data?: Assignment) => {
    const { type, value, id: fieldId = '', options = [] } = detail
    switch (type) {
      case 'text':
      case 'textarea':
      case 'addOptionSelect':
      case 'radio':
      case 'colorBlockSelect':
      case 'selectDimensions':
        if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No'
        }

        if (value === 'true') {
          return 'Yes'
        }

        if (value === 'false') {
          return 'No'
        }

        return value && value !== '' ? value : 'N/A'

      case 'checkbox':
        return Array.isArray(detail.value) ? detail.value.join(',') : 'N/A'

      case 'blockSelect':
        return value?.title || value?.name || 'N/A'

      case 'boolean':
        return value ? 'Yes' : 'No'

      case 'html':
        return (
          <div>
            {value ? (
              <HTMLRender
                value={value}
                loomHeight={loomHeight}
                loomWidth={loomWidth}
              />
            ) : (
              'N/A'
            )}
          </div>
        )

      case 'links':
      case 'social':
        if (typeof value === 'string') {
          return (
            <a
              className={styles.link}
              href={validUrl.test(value) ? value : `http://${value}`}
              target="_blank"
              rel="noreferrer"
              title={value}
            >
              {value} <ExternalLink size={14} />
            </a>
          )
        }

        return value && value.length > 0
          ? value.map((item: string, index: number) => (
              <a
                className={styles.link}
                key={`link_${index}`}
                href={validUrl.test(item) ? item : `http://${item}`}
                target="_blank"
                rel="noreferrer"
                title={item}
              >
                {item} <ExternalLink size={14} />
              </a>
            ))
          : 'N/A'

      case 'internal_links': {
        if (!Array.isArray(value)) return 'N/A'
        if (value.length === 0) return 'N/A'

        return value.map(link => {
          return (
            <a
              className={styles.link}
              key={link}
              href={link}
              target="_blank"
              rel="noreferrer"
              title={link}
            >
              {link} <ExternalLink size={14} />
            </a>
          )
        })
      }

      case 'tag':
      case 'multi-select-tags': {
        if (!Array.isArray(value)) return 'N/A'
        if (value.length === 0) return 'N/A'

        return value.map((item: string, index: number) => {
          return (
            <Tag className={styles.tag} key={`tag_${index}`}>
              {item}
            </Tag>
          )
        })
      }

      case 'file':
      case 'files': {
        let _values: Attachment[] = value

        if (fieldId === 'reference_files_artworks') {
          const _artworks = data?.reference_files.filter(
            file => file.tag === 'artworks'
          )
          _values = _artworks ? [..._artworks] : []
        }

        if (fieldId === 'reference_files') {
          const attachments = data?.reference_files.filter(file => !file.tag)
          _values = attachments ? attachments : []
        }

        if (!Array.isArray(_values)) return 'N/A'
        if (_values.length === 0) return 'N/A'

        return (
          <div className={styles.files}>
            {_values.map(attachment => {
              return (
                <FilePreviewV2
                  key={attachment.id}
                  id={attachment.id}
                  name={attachment.name}
                />
              )
            })}
          </div>
        )
      }
      case 'custom_select_search':
        return <>{assignmentDetails?.[fieldId] ?? 'N/A'}</>
      case 'select':
        if (!get(value, 'name')) {
          if (!Array.isArray(options)) return 'N/A'

          const _options = options as { label: string; value: number }[]
          const selectedOption = _options.find(
            _option => _option.value === value
          )

          return <div>{get(selectedOption, 'label', 'N/A')}</div>
        }

        return <div>{value ? value.name : 'N/A'}</div>

      case 'multi-select': {
        if (fieldId === 'platforms') {
          return value && value.length > 0
            ? value?.map((v: { name?: string }) => v?.name || v).join(', ')
            : 'N/A'
        }

        return value && value.length > 0
          ? value?.map((v: { title?: string }) => v?.title || v).join(', ')
          : 'N/A'
      }

      case 'comment-box': {
        const { comment = '', files = [] } = getDataOfCommentBox(data, fieldId)
        return comment !== '' || files.length > 0 ? (
          <div>
            <div>
              <HTMLRender value={comment} />
            </div>
            {files.length > 0 ? <Attachments attachments={files} /> : ''}
          </div>
        ) : (
          'N/A'
        )
      }

      case 'addDimensions': {
        if (Array.isArray(value) && value.length > 0) {
          return value?.map((val, index) => {
            if (val.width !== '' && val.height !== '') {
              return (
                <Tag className={styles.tagOrange} key={index}>
                  {`${val.width}`} <span className={styles.cross}>x</span>{' '}
                  {`${val.height}`}
                </Tag>
              )
            }
            return 'N/A'
          })
        }
        return 'N/A'
      }

      case 'addCustomColors': {
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className={styles.blocksContainer}>
              {value?.map((color, index) => (
                <div key={index} className={styles.customColorBox}>
                  <div
                    style={{ backgroundColor: color }}
                    className={styles.colorBlockBox}
                  ></div>
                  <div className={styles.colorCode}>{color}</div>
                </div>
              ))}
            </div>
          )
        }
        return 'N/A'
      }

      case 'multiBlockSelect': {
        if (Array.isArray(value) && value.length > 0) {
          const _values: BlockSelectOption[] = []
          const _options = [...options] as BlockSelectOption[]
          value.forEach((val: string) => {
            const _option = _options.find(
              (option: BlockSelectOption) => option.id === val
            )
            _option && _values.push(_option)
          })

          return (
            <div className={styles.blocksContainer}>
              {_values.length > 0
                ? _values?.map((block: BlockSelectOption, index: number) => (
                    <div key={index} className={styles.blocks}>
                      <AssignmentGoogleDocAttachment
                        assignmentTitle={block?.title ?? ''}
                        preview={block?.full_size_src ?? block?.icon}
                        downloadable={false}
                        fallbackImage={<File size={16} />}
                        fileUrl={block?.full_size_src ?? block?.icon}
                      />
                    </div>
                  ))
                : 'N/A'}
            </div>
          )
        }
        return 'N/A'
      }

      case 'languageSelect':
        return value ? value.replace('_', ' ') : 'N/A'

      default:
        return 'N/A'
    }
  }

  const isCreators = [
    'writer',
    'translator',
    'designer',
    'editor',
    'other'
  ].includes(user || '')

  return (
    <div className={styles.displayBriefContainer}>
      {fields.map((field, index) => {
        return (
          <div className={styles.rowContainer} key={`row_${index}`}>
            {field.map(item => {
              return (
                <div
                  key={item.title}
                  className={clsx(styles.briefItem, {
                    [styles.column]: !showNewUI,
                    [styles.row]: isOpenFromModal
                  })}
                >
                  <p className={styles.briefTitle}>
                    {isCreators && item.label ? item.label : item.title}
                    {isCreators && item.guideline && (
                      <Tooltip title={item.guideline}>
                        <AlertCircle className={styles.info} size={14} />
                      </Tooltip>
                    )}
                  </p>
                  <span
                    className={clsx({ [styles.briefItemDetail]: isCreators })}
                  >
                    {renderContentByType(item, assignmentDetails)}
                  </span>
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
